import React, { useState } from "react";
import { useFormik } from "formik";
import { useQueryClient } from "@tanstack/react-query";
import { Box } from "../../../../../../shared/Box/Box";
import styles from "../../scss/PatientFi.module.scss";
import { CircularProgress } from "../../../../../../shared/CircularProgress/CircularProgress";
import { usePfiConfigs } from "../../hooks/usePfiConfigs";
import { InputLabel } from "../../../../../../shared/InputLabel/InputLabel";
import { Select } from "../../../../../../shared/Select/Select";
import { Button } from "../../../../../../shared/Button/Button";
import { useSetPfiConfigs } from "../../hooks/useSetPfiConfigs";
import { QUERY_KEYS } from "../../../../../../consts/api";
import { uiNotification } from "../../../../../../services/UINotificationService";
import { showFormattedDate } from "../../../../../../Utils";
import DeactivateModal from "./DeactivateModal";

const ConfiguredClinics = () => {
  const [deactivateModal, setDeactivateModal] = useState(null);

  const queryClient = useQueryClient();
  const { pfiConfigs, clinics } = usePfiConfigs();
  const { mutate: setPfiConfigs, isLoading: setPfiConfigsLoading } =
    useSetPfiConfigs({
      onSuccess: (data) => {
        const successMessages = data?.data?.data?.messages;
        const errors = data?.data?.data?.errors;
        if (successMessages.length > 0) {
          successMessages.forEach((message) => {
            uiNotification.success(message);
          });
        }

        if (errors.length > 0) {
          errors.forEach((error) => {
            uiNotification.error(error);
          });
        }
        queryClient.setQueryData(
          [QUERY_KEYS.patientFi.getIntegration],
          () => data,
        );
      },
      onError: (error) => {
        uiNotification.error(
          error?.response?.data?.message || "Failed to save clinics",
        );
      },
    });

  const initialValues = pfiConfigs.reduce((acc, config) => {
    return {
      ...acc,
      [config.pfiPracticeId]: config,
    };
  }, {});

  const {
    values: pfiValues,
    setFieldValue,
    dirty,
  } = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
  });

  return (
    <Box className={styles.clinicConfigsContainer}>
      <>
        <h1>Clinics Info</h1>
        <div className={styles.clinicConfigs}>
          {Object.values(pfiValues).map((config) => {
            const selectedClinicValues = Object.values(pfiValues)
              .filter((c) => c.pfiPracticeId !== config.pfiPracticeId)
              .map((c) => c.clinic?.value)
              .filter(Boolean);

            const filteredOptions = clinics.filter(
              (clinic) => !selectedClinicValues.includes(clinic.value),
            );
            return (
              <div key={config.pfiPracticeId}>
                <InputLabel>{config.name}</InputLabel>

                {config.isConnected ? (
                  <p className={styles.authorizedText}>
                    <i className="fa fa-check-circle"></i>
                    Authorized on {showFormattedDate(config.authorisedOn, true)}
                  </p>
                ) : (
                  <p className={styles.unAuthorizedText}>
                    <i className="fa fa-times-circle"></i>
                    {config.error}
                  </p>
                )}
                <div className={styles.clinicSelectContainer}>
                  <Select
                    size="small"
                    options={filteredOptions}
                    value={config.clinic}
                    className={styles.clinicSelect}
                    onChange={(option) => {
                      setFieldValue(`${config.pfiPracticeId}.clinic`, option);
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => setDeactivateModal(config)}
                    size="small"
                  >
                    Deactivate
                  </Button>
                </div>
              </div>
            );
          })}
          <Button
            size="small"
            className={styles.saveButton}
            onClick={() =>
              setPfiConfigs({
                isActive: true,
                configs: Object.values(pfiValues)
                  .map((config) => ({
                    pfiPracticeId: config.pfiPracticeId,
                    clinicId: config.clinic?.value || 0,
                  }))
                  .filter((c) => c.clinicId > 0),
              })
            }
            leftAdornment={
              setPfiConfigsLoading ? (
                <CircularProgress size="small" color="white" />
              ) : null
            }
            isDisabled={setPfiConfigsLoading || !dirty}
          >
            Save
          </Button>
        </div>
        {deactivateModal && (
          <DeactivateModal
            config={deactivateModal}
            onClose={() => setDeactivateModal(null)}
          />
        )}
      </>
    </Box>
  );
};

export default ConfiguredClinics;
